import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  ReactNode,
  useEffect,
} from 'react';
import { useTranscript } from './TranscriptContext';
import { getWebSocketUrl } from '../utils/websocket';

export type ListeningState = 'idle' | 'listening' | 'processing';
export type AudioSourceType = 'browser' | 'hardware';

/** Sarvam batch phase (backend `batch_progress`); approximate percent until `ready_to_stop`. */
export interface AmbientProcessingProgress {
  label: string;
  percent: number;
  stage?: string;
}

interface AmbientListeningContextType {
  state: ListeningState;
  /** When `state === 'processing'`, Sarvam batch diarization step + percent (optional). */
  processingProgress: AmbientProcessingProgress | null;
  /**
   * After mic stop, WS stays open until `ready_to_stop`. When Sarvam post-stop batch is off,
   * we skip the orange "processing" UI but keep the mic disabled until the socket closes.
   */
  awaitingWsClose: boolean;
  error: string | null;
  clearError: () => void;
  startListening: (sessionId: string, audioSourceType?: AudioSourceType) => Promise<void>;
  stopListening: () => void;
}

const AmbientListeningContext = createContext<AmbientListeningContextType | undefined>(undefined);

export function AmbientListeningProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ListeningState>('idle');
  const [processingProgress, setProcessingProgress] = useState<AmbientProcessingProgress | null>(null);
  const [awaitingWsClose, setAwaitingWsClose] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { startListening: startTranscriptListening, stopListening: stopTranscriptListening } = useTranscript();

  const sessionIdRef = useRef<string | null>(null);
  const audioSourceTypeRef = useRef<AudioSourceType>('browser');
  const websocketRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<any>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const keepaliveIntervalRef = useRef<number | null>(null);
  /** Backend `/asr` tells us whether post-stop Sarvam batch (and batch_progress UI) applies. Default true for older servers. */
  const postStopBatchUiRef = useRef(true);
  /** When true, onclose must not auto-reconnect (user stopped listening). */
  const userStoppedRef = useRef(false);
  const stateRef = useRef<ListeningState>(state);
  stateRef.current = state;

  const ingestSarvamPostStopBatchFlag = useCallback((data: Record<string, unknown>) => {
    if (typeof data.sarvam_post_stop_batch_diarization === 'boolean') {
      postStopBatchUiRef.current = data.sarvam_post_stop_batch_diarization;
    }
  }, []);

  const stopListening = useCallback(() => {
    const sessionId = sessionIdRef.current;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (keepaliveIntervalRef.current) {
      clearInterval(keepaliveIntervalRef.current);
      keepaliveIntervalRef.current = null;
    }

    userStoppedRef.current = true;

    // Signal end-of-stream to backend — it will finalize (LLM extraction, validator,
    // diagnosis) as background tasks after the socket closes.
    const ws = websocketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(new Uint8Array(0)); } catch { /* ignore */ }
    }

    if (mediaRecorderRef.current) {
      const { audioContext, source, workletNode, recorderWorker } = mediaRecorderRef.current;
      try {
        if (recorderWorker) recorderWorker.terminate();
        if (workletNode) {
          workletNode.port.onmessage = null;
          workletNode.disconnect();
        }
        if (source) source.disconnect();
        if (audioContext && audioContext.state !== 'closed') audioContext.close();
      } catch (err) {
        console.error('[AmbientListening] Error stopping audio:', err);
      }
      mediaRecorderRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (sessionId && (window as any).__whisperLiveKitData) {
      try {
        localStorage.setItem(`whisperData_${sessionId}`, JSON.stringify((window as any).__whisperLiveKitData));
      } catch (e) {
        console.warn('[AmbientListening] Failed to save to localStorage');
      }
    }

    // Close immediately — backend continues processing in background.
    if (ws) {
      try { ws.close(); } catch { /* ignore */ }
      websocketRef.current = null;
    }

    sessionIdRef.current = null;
    userStoppedRef.current = false;
    setProcessingProgress(null);
    setAwaitingWsClose(false);
    setState('idle');
    stopTranscriptListening();
  }, [stopTranscriptListening]);

  const startListening = useCallback(
    async (sessionId: string, audioSourceType: AudioSourceType = 'browser') => {
      if (!sessionId) {
        setError('No active session');
        return;
      }

      // Preserve existing transcript data when reconnecting to the SAME session.
      // We only clear WhisperLiveKit data when starting a genuinely NEW session.
      const previousSessionId = sessionIdRef.current;
      sessionIdRef.current = sessionId;
      audioSourceTypeRef.current = audioSourceType;
      if (previousSessionId !== sessionId) {
        delete (window as any).__whisperLiveKitData;
        localStorage.removeItem(`whisperData_${sessionId}`);
      }
      userStoppedRef.current = false;
      setError(null);
      setProcessingProgress(null);
      setAwaitingWsClose(false);
      postStopBatchUiRef.current = true;

      // Clean up any previous audio pipeline before (re)starting to prevent
      // stale workers/contexts sending audio to a dead WebSocket on reconnect.
      if (mediaRecorderRef.current) {
        const { audioContext, source, workletNode, recorderWorker } = mediaRecorderRef.current;
        try {
          if (recorderWorker) recorderWorker.terminate();
          if (workletNode) { workletNode.port.onmessage = null; workletNode.disconnect(); }
          if (source) source.disconnect();
          if (audioContext && audioContext.state !== 'closed') audioContext.close();
        } catch { /* ignore */ }
        mediaRecorderRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }

      try {
        if (audioSourceType === 'browser') {
          if (!navigator?.mediaDevices?.getUserMedia) {
            throw new Error('Microphone access not available. Use HTTPS or localhost.');
          }
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          });
          streamRef.current = stream;
        }

        const wsUrl = getWebSocketUrl(`/asr?session_id=${sessionId}`);
        const ws = new WebSocket(wsUrl);

        await new Promise<void>((resolve, reject) => {
          ws.onopen = () => {
            setState('listening');
            startTranscriptListening();
            resolve();
          };
          ws.onerror = () => reject(new Error('WebSocket connection failed'));
          setTimeout(() => reject(new Error('WebSocket connection timeout')), 5000);
        });

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data) as Record<string, unknown>;
            ingestSarvamPostStopBatchFlag(data);
            if (data.lines !== undefined || data.type === 'final_diarized_transcript') {
              const linesToShow = Array.isArray(data.full_lines)
                ? data.full_lines
                : Array.isArray(data.lines)
                  ? data.lines
                  : null;
              if (linesToShow && linesToShow.length > 0) {
                (window as any).__whisperLiveKitData = { ...data, lines: linesToShow };
                (window as any).__whisperLiveKitSessionId =
                  sessionId != null ? String(sessionId).toLowerCase() : undefined;
              }
            } else if (data.type === 'error') {
              const msg = data.message;
              setError(typeof msg === 'string' ? msg : 'Error');
            }
          } catch (_) {}
        };

        ws.onclose = () => {
          if (userStoppedRef.current) {
            userStoppedRef.current = false;
            return;
          }
          if (stateRef.current === 'listening' && sessionIdRef.current === sessionId) {
            const sid = sessionIdRef.current;
            const ast = audioSourceTypeRef.current;
            reconnectTimeoutRef.current = window.setTimeout(() => {
              if (stateRef.current === 'listening' && sessionIdRef.current === sid) {
                startListening(sid, ast);
              }
            }, 3000);
          }
        };

        websocketRef.current = ws;

        // Send a tiny silent PCM frame every 25s to prevent proxy/nginx idle-close.
        // Must be binary non-empty: receive_bytes() rejects text; len==0 is end-of-stream signal.
        if (keepaliveIntervalRef.current) clearInterval(keepaliveIntervalRef.current);
        keepaliveIntervalRef.current = window.setInterval(() => {
          const w = websocketRef.current;
          if (w && w.readyState === WebSocket.OPEN) {
            try { w.send(new Uint8Array(4).buffer); } catch { /* ignore */ }
          }
        }, 25_000);

        if (audioSourceType === 'browser' && streamRef.current) {
          // Capture before any await: stopListening / Strict Mode can clear streamRef while addModule runs.
          const micStream = streamRef.current;
          const audioContext = new AudioContext();
          await audioContext.audioWorklet.addModule('/audio/pcm_worklet.js');
          const source = audioContext.createMediaStreamSource(micStream);
          const workletNode = new AudioWorkletNode(audioContext, 'pcm-forwarder', {
            numberOfInputs: 1,
            numberOfOutputs: 0,
            channelCount: 1,
          });
          source.connect(workletNode);

          const recorderWorker = new Worker('/audio/recorder_worker.js');
          recorderWorker.postMessage({ command: 'init', config: { sampleRate: audioContext.sampleRate, targetSampleRate: 16000 } });

          workletNode.port.onmessage = (e: MessageEvent) => {
            const arrayBuffer = (e.data instanceof ArrayBuffer ? e.data : (e.data as any).buffer) as ArrayBuffer;
            recorderWorker.postMessage({ command: 'record', buffer: arrayBuffer }, [arrayBuffer]);
          };

          recorderWorker.onmessage = (e: MessageEvent) => {
            if (ws.readyState === WebSocket.OPEN) ws.send(e.data.buffer);
          };

          mediaRecorderRef.current = { audioContext, source, workletNode, recorderWorker };
        }
      } catch (err: any) {
        const isPermDenied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
        setError(
          isPermDenied
            ? 'Microphone access denied. Please allow microphone permission in your browser settings and try again.'
            : err?.message || 'Failed to start listening'
        );
        stopListening();
      }
    },
    [startTranscriptListening, stopListening, ingestSarvamPostStopBatchFlag]
  );

  useEffect(() => {
    return () => {
      stopListening();
    };
  }, [stopListening]);

  const clearError = useCallback(() => setError(null), []);

  return (
    <AmbientListeningContext.Provider
      value={{
        state,
        processingProgress,
        awaitingWsClose,
        error,
        clearError,
        startListening,
        stopListening,
      }}
    >
      {children}
    </AmbientListeningContext.Provider>
  );
}

export function useAmbientListening() {
  const ctx = useContext(AmbientListeningContext);
  if (ctx === undefined) {
    throw new Error('useAmbientListening must be used within AmbientListeningProvider');
  }
  return ctx;
}
